import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { MdSearch } from 'react-icons/md';

import Loading from 'components/common/loading/Loading';

import { SKILLS } from 'constants/permissions';

import * as skillService from 'services/skillService';
import * as skillCategoryService from 'services/skillCategoryService';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import { handleError } from 'utils/errorHandler';
import * as toast from 'utils/toast';

import { SkillSetV1, EditSkill, EditSkillCategory } from './components';

import type { PermissionMap } from 'pages/settings/users/userPermissions/types';
import type {
  LegacySkill,
  LegacySkillCategory,
  SkillCategoryFormValues,
  SkillFormValues,
  SkillsV1Props,
} from './types';

type SkillListResponse = {
  data: LegacySkill[];
};

type SkillCategoryListResponse = {
  data: LegacySkillCategory[];
};

const toFormSkillValues = (skill: LegacySkill): SkillFormValues => ({
  id: skill.id == null ? undefined : Number(skill.id),
  name: skill.name,
  description: skill.description ?? '',
  skillCategory: skill.skillCategory,
});

const Skills: React.FC<SkillsV1Props> = ({ user }) => {
  const permissions = useUserPermissions() as PermissionMap;

  const [skills, setSkills] = useState<LegacySkill[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeSkill, setActiveSkill] = useState<SkillFormValues | null>(null);
  const [activeSkillCategory, setActiveSkillCategory] = useState<SkillCategoryFormValues | null>(null);
  const [skillCategories, setSkillCategories] = useState<LegacySkillCategory[]>([]);
  const [showEditSkill, setShowEditSkill] = useState(false);
  const [showEditSkillCategory, setShowEditSkillCategory] = useState(false);

  useEffect(() => {
    window.document.title = 'Skills | Vyaguta';
  }, []);

  const fetchSkillCategories = async () => {
    try {
      setIsLoading(true);
      const { data } = (await skillCategoryService.fetchAll({ size: 10000 })) as SkillCategoryListResponse;

      if (!data?.length) {
        setSkillCategories([]);
        setIsLoading(false);

        return;
      }

      setSkillCategories(data);
      setIsLoading(false);
    } catch (error) {
      handleError(error);
      setIsLoading(false);
    }
  };

  const fetchSkills = async () => {
    try {
      setIsLoading(true);

      const skillResponse = (await skillService.fetchAllV1()) as SkillListResponse;

      setActiveSkill(null);
      setShowEditSkill(false);
      setShowEditSkillCategory(false);
      setSkills(skillResponse.data);
      setIsSaving(false);
      setIsDeleting(false);
      setIsLoading(false);
    } catch (error) {
      handleError(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchSkillCategories();
      await fetchSkills();
    };

    void loadData();
  }, []);

  const handleSkillAdd = (skillCategory: LegacySkillCategory) => {
    if (activeSkill && !activeSkill.id && activeSkill.skillCategory?.id === skillCategory.id) {
      handleSkillCancel();

      return;
    }

    setActiveSkill({
      name: '',
      description: '',
      skillCategory,
    });
    setShowEditSkill(true);
  };

  const handleSkillClick = (selectedSkill: LegacySkill) => {
    if (showEditSkillCategory) {
      handleSkillCategoryCancel();
    }

    const nextActiveSkill = activeSkill?.id === Number(selectedSkill.id) ? null : toFormSkillValues(selectedSkill);

    setActiveSkill(nextActiveSkill);
    setShowEditSkill(Boolean(nextActiveSkill));
  };

  const handleSkillCancel = () => {
    setShowEditSkill(false);
    setActiveSkill(null);
  };

  const handleSkillSave = async (skill: SkillFormValues, id: number | null = null) => {
    if (!skill.skillCategory) {
      toast.error({ title: 'Error', message: 'Please enter the skill category' });

      return;
    }

    setIsSaving(true);

    try {
      const skillPayload = {
        name: skill.name,
        description: skill.description ?? null,
        skillCategory: {
          id: skill.skillCategory.id ?? 0,
          name: skill.skillCategory.name,
        },
      };

      if (id) {
        await skillService.update(skillPayload, id);
      } else {
        await skillService.create(skillPayload);
      }

      await fetchSkills();

      toast.success({
        title: 'Success',
        message: `Skill has been ${id ? 'updated' : 'created'} successfully.`,
      });
    } catch (error) {
      handleError(error);
      setIsSaving(false);
    }
  };

  const handleSkillDelete = async (skill: SkillFormValues) => {
    if (!skill.id) {
      return;
    }

    setIsDeleting(true);

    try {
      await skillService.remove(skill.id);
      await fetchSkills();

      toast.success({
        title: 'Success',
        message: 'Skill has been deleted successfully.',
      });
    } catch (error) {
      handleError(error, { message: 'Cannot delete the selected skill.' });
      setIsDeleting(false);
    }
  };

  const handleSkillCategoryCancel = () => {
    setShowEditSkillCategory(false);
    setActiveSkillCategory(null);
  };

  const handleSkillCategorySave = async (skillCategory: SkillCategoryFormValues, id: number | null = null) => {
    setIsSaving(true);

    try {
      const skillCategoryPayload = {
        name: skillCategory.name,
        description: skillCategory.description ?? null,
      };

      if (id) {
        await skillCategoryService.update(skillCategoryPayload, id);
      } else {
        await skillCategoryService.create(skillCategoryPayload);
      }

      await fetchSkillCategories();

      toast.success({
        title: 'Success',
        message: `Skill Category has been ${id ? 'updated' : 'created'} successfully.`,
      });

      setShowEditSkillCategory(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkillCategoryDelete = async (id: number) => {
    setIsDeleting(true);

    try {
      await skillCategoryService.remove(id);
      await fetchSkillCategories();

      toast.success({
        title: 'Success',
        message: 'Skill Category has been deleted successfully.',
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Commented out for skillv1
  //   const handleSkillCategoryAddClick = () => {
  //     if (showEditSkill) {
  //       handleSkillCancel();
  //     }

  //     setShowEditSkillCategory((previousState) => !previousState);
  //   };

  //   const handleSkillAddClick = () => {
  //     if (showEditSkillCategory) {
  //       handleSkillCategoryCancel();
  //     }

  //     if (showEditSkill && !activeSkill?.id) {
  //       handleSkillCancel();

  //       return;
  //     }

  //     setActiveSkill({
  //       name: '',
  //       description: '',
  //       skillCategory: null,
  //     });
  //     setShowEditSkill(true);
  //   };

  const handleSearchChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(value);
  };

  const handleSkillCategorySelect = (category: LegacySkillCategory) => {
    if (showEditSkill) {
      handleSkillCancel();
    }

    if (showEditSkillCategory && activeSkillCategory?.id === category.id) {
      handleSkillCategoryCancel();
    } else {
      setActiveSkillCategory(category);
      setShowEditSkillCategory(true);
    }
  };

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div>
          <div className="table-title">Skills</div>
          <div className="table-subtitle">{`Showing ${skills.length} Results`}</div>
        </div>

        <div className="d-flex flex-row">
          <div className="search-wrapper skills-search d-flex flex-row align-items-center">
            <MdSearch className="search-icon" />
            <input
              className="search-bar skills-search-input"
              value={searchQuery}
              type="text"
              onChange={handleSearchChange}
              placeholder="Search by keywords"
            />
          </div>
          {/* Commented out for skill version 1 */}
          {/* eslint-disable-next-line no-trailing-spaces */}
          {/* <button 
          className="btn btn--primary f-left card-button mr-10 new-category-btn" onClick={handleSkillCategoryAddClick}>
            Add Category
          </button>

          <button className="btn btn--primary f-left card-button mr-10 new-skill-btn" onClick={handleSkillAddClick}>
            Add Skill
          </button> */}
        </div>
      </div>
      <div className={classNames({ loading__container: isLoading })}>
        <div className="full-scope-card__content d-flex position-relative">
          {isLoading ? (
            <Loading className="mt-120" />
          ) : (
            <>
              <div className="skill-set-area">
                <SkillSetV1
                  skillCategories={skillCategories}
                  searchQuery={searchQuery}
                  skills={skills}
                  activeSkill={activeSkill}
                  onSkillCategoryClick={handleSkillCategorySelect}
                  onSkillClick={handleSkillClick}
                  onSkillAdd={handleSkillAdd}
                  permissions={permissions}
                  isHr={user.isHr}
                />
              </div>
              {permissions[SKILLS.UPDATE] && (
                <div className="skill-edit-area">
                  {showEditSkillCategory && (
                    <EditSkillCategory
                      skillCategory={activeSkillCategory}
                      isHr={user.isHr}
                      handleSave={handleSkillCategorySave}
                      handleDelete={handleSkillCategoryDelete}
                      handleCancel={handleSkillCategoryCancel}
                      saving={isSaving}
                      deleting={isDeleting}
                    />
                  )}
                  {showEditSkill && !(activeSkillCategory && !activeSkillCategory.id) && (
                    <EditSkill
                      skill={activeSkill}
                      isHr={user.isHr}
                      skillCategories={skillCategories}
                      handleSave={handleSkillSave}
                      handleDelete={handleSkillDelete}
                      handleCancel={handleSkillCancel}
                      saving={isSaving}
                      deleting={isDeleting}
                    />
                  )}
                  {!showEditSkill && !showEditSkillCategory && (
                    <div className="empty-skill-set">Click on any of the skill to view details here</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Skills;
